import React, { useEffect, useState } from "react";
import "./AuditLog.css";
import { ToastContainer, toast } from "react-toastify";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { UilAngleLeft, UilAngleRight, UilEye } from "@iconscout/react-unicons";
import { userRequest } from "../../RequestMethods";

const ENTITY_TYPES = ["", "Worker", "Role", "Session"];
const ACTIONS = ["", "create", "update", "delete", "login", "login_failed", "permission_change"];

const AuditLog = () => {
  const [logs, setLogs] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedLogId, setExpandedLogId] = useState(null);

  const [filters, setFilters] = useState({
    entityType: "",
    action: "",
    from: "",
    to: "",
  });

  const fetchLogs = async (page = 1) => {
    try {
      setLoading(true);
      const params = { page, limit: pagination.limit };
      if (filters.entityType) params.entityType = filters.entityType;
      if (filters.action) params.action = filters.action;
      if (filters.from) params.from = filters.from;
      if (filters.to) params.to = filters.to;

      const res = await userRequest.get("audit-logs", { params });
      setLogs(res.data.logs || []);
      setPagination(res.data.pagination || { page: 1, limit: 25, total: 0, pages: 1 });
      setError(null);
    } catch (err) {
      const message = err?.response?.data?.message || "Erreur lors du chargement du journal d'audit.";
      if (logs.length === 0) setError(message);
      else toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const handleApplyFilters = (e) => {
    e.preventDefault();
    fetchLogs(1);
  };

  const handlePrevPage = () => {
    if (pagination.page > 1) fetchLogs(pagination.page - 1);
  };

  const handleNextPage = () => {
    if (pagination.page < pagination.pages) fetchLogs(pagination.page + 1);
  };

  if (loading && logs.length === 0) {
    return (
      <div className="AuditLog">
        <div className="loading-container">
          <div className="loading-spinner-custom"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="AuditLog">
        <div className="error-message-custom">
          <span className="error-icon">⚠️</span>
          <p>{error}</p>
          <button className="retry-btn" onClick={() => fetchLogs(1)}>
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="AuditLog">
      <h1>Journal d'audit</h1>

      <form className="audit-filters" onSubmit={handleApplyFilters}>
        <select name="entityType" value={filters.entityType} onChange={handleFilterChange}>
          <option value="">Toutes les entités</option>
          {ENTITY_TYPES.filter(Boolean).map((type) => (
            <option key={type} value={type}>{type}</option>
          ))}
        </select>

        <select name="action" value={filters.action} onChange={handleFilterChange}>
          <option value="">Toutes les actions</option>
          {ACTIONS.filter(Boolean).map((action) => (
            <option key={action} value={action}>{action}</option>
          ))}
        </select>

        <input type="date" name="from" value={filters.from} onChange={handleFilterChange} />
        <input type="date" name="to" value={filters.to} onChange={handleFilterChange} />

        <button type="submit" className="action-icon-btn btn-primary">Filtrer</button>
      </form>

      <TableContainer component={Paper} className="audit-table-container">
        <Table>
          <TableHead>
            <TableRow className="table-header">
              <TableCell sx={{ fontWeight: "bold" }}>Date</TableCell>
              <TableCell>Acteur</TableCell>
              <TableCell>Entité</TableCell>
              <TableCell>Action</TableCell>
              <TableCell>IP</TableCell>
              <TableCell>Détails</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {logs.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">Aucune entrée trouvée.</TableCell>
              </TableRow>
            )}
            {logs.map((log) => (
              <React.Fragment key={log._id}>
                <TableRow>
                  <TableCell>{new Date(log.createdAt).toLocaleString("fr-FR")}</TableCell>
                  <TableCell>{log.actorEmail || "—"}</TableCell>
                  <TableCell>
                    {log.entityType} <span className="entity-id">#{String(log.entityId).slice(-6)}</span>
                  </TableCell>
                  <TableCell>
                    <span className={`badge action-${log.action}`}>{log.action}</span>
                  </TableCell>
                  <TableCell>{log.ipAddress || "—"}</TableCell>
                  <TableCell>
                    {(log.before || log.after) && (
                      <button
                        className="action-icon-btn btn-edit"
                        onClick={() => setExpandedLogId(expandedLogId === log._id ? null : log._id)}
                      >
                        <UilEye /> Voir
                      </button>
                    )}
                  </TableCell>
                </TableRow>
                {expandedLogId === log._id && (
                  <TableRow>
                    <TableCell colSpan={6} className="details-cell">
                      <div className="details-grid">
                        <div>
                          <h4>Avant</h4>
                          <pre>{JSON.stringify(log.before, null, 2) || "—"}</pre>
                        </div>
                        <div>
                          <h4>Après</h4>
                          <pre>{JSON.stringify(log.after, null, 2) || "—"}</pre>
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <div className="audit-pagination">
        <button className="pagination-btn" onClick={handlePrevPage} disabled={pagination.page <= 1}>
          <UilAngleLeft /> Précédent
        </button>
        <span>Page {pagination.page} / {pagination.pages || 1} ({pagination.total} entrées)</span>
        <button className="pagination-btn" onClick={handleNextPage} disabled={pagination.page >= pagination.pages}>
          Suivant <UilAngleRight />
        </button>
      </div>

      <ToastContainer position="top-right" />
    </div>
  );
};

export default AuditLog;
